import { Trash2, CreditCard } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../../ui/alert-dialog";
import { formatCurrency } from "../utils";
import { formatDate } from "../../../lib/formatters";

/**
 * Single payment row display
 */
const PaymentRow = ({ payment, canManageJobs, onDeletePayment, billingId }) => {
  return (
    <div className="flex items-center justify-between bg-muted/30 p-2 rounded">
      <div className="flex items-center gap-4">
        <span className="font-mono text-green-600 font-medium">
          {formatCurrency(payment.amount)}
        </span>
        <span className="text-sm text-muted-foreground">{formatDate(payment.date)}</span>
        {payment.method && (
          <Badge variant="outline" className="text-xs">
            {payment.method.toUpperCase()}
          </Badge>
        )}
        {payment.notes && (
          <span className="text-sm text-muted-foreground truncate max-w-xs">
            {payment.notes}
          </span>
        )}
      </div>
      {canManageJobs && (
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive">
              <Trash2 className="w-3 h-3" />
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Payment?</AlertDialogTitle>
              <AlertDialogDescription>
                This will remove the {formatCurrency(payment.amount)} payment from {formatDate(payment.date)}.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction 
                onClick={() => onDeletePayment(billingId, payment.id)}
                className="bg-destructive text-destructive-foreground"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
};

/**
 * Payment history section showing all payments grouped by billing
 */
const PaymentHistorySection = ({ activeBillings, canManageJobs, onDeletePayment }) => {
  const billingsWithPayments = (activeBillings || []).filter(b => (b?.payments?.length || 0) > 0);
  
  if (billingsWithPayments.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <CreditCard className="w-5 h-5" />
          Payment History
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {billingsWithPayments.map(billing => (
            <div key={billing.id} className="border rounded-lg p-4">
              <p className="font-medium mb-2">Application #{billing.billing_number}</p>
              <div className="space-y-2">
                {billing.payments.map(payment => (
                  <PaymentRow
                    key={payment.id}
                    payment={payment}
                    canManageJobs={canManageJobs}
                    onDeletePayment={onDeletePayment}
                    billingId={billing.id}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default PaymentHistorySection;
